import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Note: Authentication is now handled via cookies shared with main app
// No need to manually add authorization headers

// Add response interceptor to handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Redirect to main app login if unauthorized
            window.location.href = process.env.REACT_APP_MAIN_APP_URL || 'https://app.rannkly.rank/login';
        }
        return Promise.reject(error);
    }
);

// Auth API calls
// Note: Registration and login are now handled by the main app at app.rannkly.rank
export const authAPI = {
    getMe: () => api.get('/auth/me'),
};

// Reviews API calls
export const reviewsAPI = {
    getReviews: (params) => api.get('/reviews', { params }),
    getReviewsByPlatform: () => api.get('/reviews/platforms'),
    getAnalytics: () => api.get('/reviews/analytics'),
    createReview: (reviewData) => api.post('/reviews', reviewData),
    bulkCreateReviews: (reviews) => api.post('/reviews/bulk', { reviews }),
    updateReview: (id, reviewData) => api.put(`/reviews/${id}`, reviewData),
    deleteReview: (id) => api.delete(`/reviews/${id}`),
};

// Locations API calls
export const locationsAPI = {
    getLocations: () => api.get('/locations'),
    getLocation: (id) => api.get(`/locations/${id}`),
    getLocationPlatforms: (locationIds) => api.get('/locations/platforms', { params: { locationIds } }),
    getLocationsForWidget: () => api.get('/locations/widget'), // New endpoint for widget creation
    syncLocations: () => api.post('/locations/sync'), // Manual sync endpoint
    createLocation: (locationData) => api.post('/locations', locationData),
    updateLocation: (id, locationData) => api.put(`/locations/${id}`, locationData),
    deleteLocation: (id) => api.delete(`/locations/${id}`),
};

// Templates API calls
export const templatesAPI = {
    getTemplatesByType: (type = 'reviews') => api.get('/templates', { params: { type } }),
    getAllTemplates: () => api.get('/templates/all'),
    getTemplatesByCategory: (category, type = 'reviews') => api.get('/templates/category', { params: { category, type } }),
    getTemplate: (id) => api.get(`/templates/${id}`),
    getTemplateBySlug: (slug) => api.get(`/templates/slug/${slug}`),
    createTemplate: (templateData) => api.post('/templates', templateData),
    updateTemplate: (id, templateData) => api.put(`/templates/${id}`, templateData),
    deleteTemplate: (id) => api.delete(`/templates/${id}`),
    toggleTemplateStatus: (id) => api.patch(`/templates/${id}/toggle-status`),
};

// Widgets API calls
export const widgetsAPI = {
    getWidgets: () => api.get('/widgets'),
    getWidget: (id) => api.get(`/widgets/${id}`),
    getWidgetPreview: (id) => api.get(`/widgets/${id}/preview`),
    getWidgetTemplates: () => api.get('/widgets/templates'),
    getPlatformCounts: (locationIds) => api.get('/widgets/platform-counts', { params: { locationIds } }),
    createWidget: (widgetData) => api.post('/widgets', widgetData),
    updateWidget: (id, widgetData) => api.put(`/widgets/${id}`, widgetData),
    updateWidgetName: (id, name) => api.patch(`/widgets/${id}/name`, { name }),
    updateWidgetStatus: (id, isActive) => api.patch(`/widgets/${id}/status`, { isActive }),
    deleteWidget: (id) => api.delete(`/widgets/${id}`),
};

// Dashboard API calls
export const dashboardAPI = {
    getDashboardMetrics: () => api.get('/analytics/dashboard'),
    getLocationReviews: () => api.get('/reviews/location-breakdown'),
};

export { API_URL };
export default api; 